import React, { useState } from 'react';
import { makeUserSuperAdmin, listAdminUsers } from '../../utils/adminUtils';
import { Box, Button, Typography, TextField, Paper, Alert, List, ListItem, ListItemText, Chip, CircularProgress } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../firebase.marketplace';

const AdminTools = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAdmins, setShowAdmins] = useState(false);
  const { currentUser, hasRole } = useAuth();

  // Check if current user is an admin
  const isAdmin = hasRole(ROLES.ADMIN);

  const handleMakeSuperAdmin = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      const updatedUser = await makeUserSuperAdmin(email);
      setSuccess(true);
      setEmail('');
      console.log('User updated:', updatedUser);
    } catch (error) {
      setError(error.message || 'Failed to make user a super admin');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleListAdmins = async () => {
    setLoading(true);
    setError('');
    
    try {
      const admins = await listAdminUsers();
      setAdminUsers(admins);
      setShowAdmins(true);
    } catch (error) {
      setError(error.message || 'Failed to list admin users');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error">
          You don't have permission to access this page. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <AdminPanelSettingsIcon sx={{ mr: 1 }} />
        Admin Tools
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Make User Super Admin
        </Typography>
        
        <Box component="form" onSubmit={handleMakeSuperAdmin} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="User Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter user email"
            margin="normal"
            variant="outlined"
            disabled={loading}
          />
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Make Super Admin'}
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            User has been successfully made a super admin!
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Admin Users
        </Typography>
        
        <Button 
          variant="outlined" 
          onClick={handleListAdmins}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'List Admin Users'}
        </Button>
        
        {showAdmins && (
          <Box sx={{ mt: 2 }}>
            {adminUsers.length === 0 ? (
              <Alert severity="info">No admin users found</Alert>
            ) : (
              <List>
                {adminUsers.map((user) => (
                  <ListItem key={user.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1 }} />
                          {user.displayName || user.email}
                        </Box>
                      }
                      secondary={user.email}
                    />
                    <Box>
                      {user.roles && Object.keys(user.roles).map((role) => (
                        user.roles[role] && (
                          <Chip 
                            key={role} 
                            label={role} 
                            color={role === 'superadmin' ? 'error' : 'primary'} 
                            size="small" 
                            sx={{ mr: 0.5 }} 
                          />
                        )
                      ))}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AdminTools;
